import { useState } from 'react';
import { Laptop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepDesktopHandoffProps {
  onSubmit: () => void;
  onSkip: () => void;
  isSubmitting: boolean;
}

const LINE_EMAIL_DOMAINS = ['line.me', 'linecorp.com'];

function isLineEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();
  return LINE_EMAIL_DOMAINS.some((d) => domain === d);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function StepDesktopHandoff({ onSubmit, onSkip, isSubmitting }: StepDesktopHandoffProps) {
  const { updateFormData, formData } = useRegistrationStore();
  const [email, setEmail] = useState(formData.realEmail || '');
  const [error, setError] = useState<string | null>(null);

  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const handleSubmit = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError(t.desktopHandoffEmailRequired);
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError(t.desktopHandoffEmailInvalid);
      return;
    }
    if (isLineEmail(trimmed)) {
      setError(t.desktopHandoffLineEmailNudge);
      return;
    }
    updateFormData({ realEmail: trimmed });
    onSubmit();
  };

  const handleSkip = () => {
    updateFormData({ realEmail: '' });
    onSkip();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex md:justify-center mb-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Laptop className="w-8 h-8 text-primary" />
          </div>
        </div>
        <h2 className="text-xl font-semibold text-foreground md:text-center">
          {t.desktopHandoffTitle}
        </h2>
      </div>

      <div className="space-y-3">
        <Input
          type="email"
          placeholder={t.desktopHandoffEmailPlaceholder}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          className="h-12 rounded-md text-base"
        />
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-12 rounded-md text-base font-medium"
        >
          {isSubmitting ? t.creatingWorkspace : t.desktopHandoffSendLink}
        </Button>
        <button
          type="button"
          onClick={handleSkip}
          disabled={isSubmitting}
          className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        >
          {t.desktopHandoffSkip}
        </button>
      </div>
    </div>
  );
}
